import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Search, Users, UserCircle, MapPin, Hand, Star, BadgeCheck, X, Phone, Mail, Cake, ShieldAlert } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { fileUrl } from "@/components/ImageUpload";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import VerifiedBadge from "@/components/VerifiedBadge";
import api from "@/lib/api";

const inputCls = "rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const SKILLS = ["2.0", "2.5", "3.0", "3.5", "4.0", "4.5", "5.0+"];
const HANDS = { right: "Right", left: "Left", ambidextrous: "Ambidextrous" };
const GENDERS = { male: "Male", female: "Female", mixed: "Non-binary / Other", prefer_not: "Prefer not to say" };
const PREFS = { singles: "Singles", doubles: "Doubles", mixed: "Mixed Doubles", recreational: "Recreational", competitive: "Competitive" };

export default function PlayerDirectory() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialTid = params.get("tid") || "";
  const [scope, setScope] = useState(initialTid ? "mine" : "all");
  const [q, setQ] = useState("");
  const [skill, setSkill] = useState("");
  const [hand, setHand] = useState("");
  const [gender, setGender] = useState("");
  const [hasDupr, setHasDupr] = useState(false);
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setRows(null);
    const p = { scope, q, skill, hand, gender, has_dupr: hasDupr };
    if (initialTid) p.tid = initialTid;
    api.get("/players/directory", { params: p }).then(({ data }) => setRows(data)).catch(() => setRows(false));
  }, [scope, q, skill, hand, gender, hasDupr, initialTid]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  if (user && user.account_type === "player") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Player Directory" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grain">
        <div className="flex items-center gap-2 mb-6">
          <Users className="w-6 h-6 text-primary-c" />
          <h1 className="font-display font-bold text-2xl">Player Directory</h1>
          {Array.isArray(rows) && <span className="text-xs mono glass rounded-full px-2.5 py-0.5" style={{ color: "var(--text-2)" }}>{rows.length}</span>}
        </div>

        {/* scope toggle */}
        <div className="glass rounded-full p-0.5 inline-flex gap-0.5 mb-4" data-testid="directory-scope">
          {[["all", "All players"], ["mine", initialTid ? "This tournament" : "My entrants"]].map(([v, l]) => (
            <button key={v} data-testid={`scope-${v}`} onClick={() => setScope(v)}
              className="rounded-full px-4 py-1.5 text-xs font-bold transition-colors"
              style={scope === v ? { background: "var(--primary)", color: "var(--on-primary)" } : { color: "var(--text-2)" }}>{l}</button>
          ))}
        </div>

        {/* filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-c" />
            <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, city or email" className={`${inputCls} w-full pl-9`} style={{ color: "var(--text)" }} />
          </div>
          <select data-testid="filter-skill" value={skill} onChange={(e) => setSkill(e.target.value)} className={inputCls} style={{ color: "var(--text)" }}>
            <option value="">Any skill</option>{SKILLS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-hand" value={hand} onChange={(e) => setHand(e.target.value)} className={inputCls} style={{ color: "var(--text)" }}>
            <option value="">Any hand</option>{Object.entries(HANDS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="filter-gender" value={gender} onChange={(e) => setGender(e.target.value)} className={inputCls} style={{ color: "var(--text)" }}>
            <option value="">Any gender</option>{Object.entries(GENDERS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button data-testid="filter-dupr" onClick={() => setHasDupr((v) => !v)}
            className="rounded-xl px-3 py-2.5 text-sm font-bold flex items-center gap-1.5 transition-colors"
            style={hasDupr ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--surface)", color: "var(--text-2)", border: "1px solid var(--border)" }}>
            <BadgeCheck className="w-4 h-4" /> Has DUPR
          </button>
        </div>

        {rows === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>
        ) : rows === false ? (
          <p className="text-sm py-20 text-center" style={{ color: "var(--text-2)" }}>Could not load directory.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm py-20 text-center" style={{ color: "var(--text-2)" }} data-testid="directory-empty">No players match your filters.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
            {rows.map((r) => <PlayerCard key={r.user_id} r={r} onClick={() => setSelected(r)} />)}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="glass max-w-md" style={{ background: "var(--surface-glass)" }} data-testid="player-detail">
          <DialogTitle className="sr-only">Player details</DialogTitle>
          {selected && <PlayerDetail r={selected} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Avatar({ r, size = 48 }) {
  const url = r.profile?.profile_photo ? fileUrl(r.profile.profile_photo) : r.picture;
  return url
    ? <img src={url} alt="" className="rounded-full object-cover glow-border" style={{ width: size, height: size }} />
    : <div className="rounded-full glass flex items-center justify-center" style={{ width: size, height: size }}><UserCircle className="w-6 h-6 text-primary-c" /></div>;
}

function PlayerCard({ r, onClick }) {
  const p = r.profile || {};
  const loc = p.location || {};
  return (
    <button data-testid={`player-card-${r.user_id}`} onClick={onClick}
      className="text-left rounded-2xl glow-border p-4 hover:scale-[1.01] transition-transform flex gap-3" style={{ background: "var(--surface)" }}>
      <Avatar r={r} />
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold truncate flex items-center gap-1">{r.name}<VerifiedBadge verified={r.verified} testid={`directory-verified-${r.user_id}`} /></div>
        <div className="text-xs flex items-center gap-1 truncate" style={{ color: "var(--text-2)" }}><MapPin className="w-3 h-3" />{loc.city || "—"}</div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {p.self_rated_skill && <span className="text-[10px] mono glass rounded-full px-2 py-0.5 flex items-center gap-1"><Star className="w-3 h-3 text-primary-c" />{p.self_rated_skill}</span>}
          {p.dominant_hand && <span className="text-[10px] mono glass rounded-full px-2 py-0.5 flex items-center gap-1"><Hand className="w-3 h-3 text-primary-c" />{HANDS[p.dominant_hand] || p.dominant_hand}</span>}
          {p.dupr_id && <span className="text-[10px] mono glass rounded-full px-2 py-0.5 flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-primary-c" />DUPR</span>}
        </div>
      </div>
    </button>
  );
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 text-primary-c mt-0.5 shrink-0" />
      <span style={{ color: "var(--text-2)" }} className="w-28 shrink-0">{label}</span>
      <span className="font-medium break-words">{value}</span>
    </div>
  );
}

function PlayerDetail({ r }) {
  const p = r.profile || {};
  const loc = p.location || {};
  const ec = p.emergency_contact || {};
  const prefs = (p.playing_preferences || []).map((x) => PREFS[x] || x).join(", ");
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Avatar r={r} size={64} />
        <div><div className="font-display font-bold text-lg flex items-center gap-1.5">{r.name}<VerifiedBadge verified={r.verified} size={18} /></div><div className="text-xs" style={{ color: "var(--text-2)" }}>{GENDERS[p.gender] || ""}</div></div>
      </div>
      {p.facebook_url && (
        <a href={p.facebook_url} target="_blank" rel="noreferrer" data-testid="player-detail-fb"
          className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 glass" style={{ color: "#3b82f6" }}>
          View Facebook {r.facebook_verified ? "✓" : ""}
        </a>
      )}
      <div className="space-y-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
        <Row icon={Star} label="Skill" value={p.self_rated_skill} />
        <Row icon={Hand} label="Dominant hand" value={HANDS[p.dominant_hand]} />
        <Row icon={BadgeCheck} label="DUPR" value={p.dupr_id} />
        <Row icon={Users} label="Prefers" value={prefs} />
        <Row icon={MapPin} label="Location" value={[loc.city, loc.zip].filter(Boolean).join(", ")} />
        <Row icon={Cake} label="Date of birth" value={p.date_of_birth} />
        <Row icon={Mail} label="Email" value={r.email} />
        <Row icon={Phone} label="Phone" value={p.phone} />
        <Row icon={ShieldAlert} label="Emergency" value={[ec.name, ec.phone].filter(Boolean).join(" · ")} />
      </div>
    </div>
  );
}
